// toolStepLabels — the human copy for every agent tool step (memory: agent
// voice is concise + warm; no dev jargon on user surfaces). Without it a step
// would render its raw server tool id (`list_sources`, `query_triples`, …).
//
// ONE typed catalogue, one entry per tool in packages/server/src/tools/**:
//   - `doing`: present tense, for the live row (D51) and a running step
//     ("Reading the source");
//   - `doingOn`: present tense naming the call's object once known, in the
//     owner's label form ("Reading source: alvesson.pdf"). Absent where the
//     plain `doing` reads fine before ": <object>";
//   - `done`: past tense, for the settled trail ("Read the source");
//   - `doneCounted`: past tense counting several distinct objects (G10);
//   - `byMaterialKind`: a creating call's doing-copy once its input reveals
//     the kind it makes ("Creating quiz: Sannolikhetslära").
// The past form is hand-written, not derived: English has irregular pasts and
// each form is independently localized copy.
//
// Pure: every label is a Lingui `MessageDescriptor`, never a localized string;
// the caller localizes with `i18n._`. The key space is a plain `string` (the
// domain's `ToolName` brand doesn't cross into UI props), so a tool this table
// hasn't caught up with still renders a generic human phrase, never its id.
// toolStepLabels.test.ts reads the server tool source and asserts every tool id
// there has its own entry, translated in both locales.

export type { ToolStepVerbMessages };
export {
    firstPersonStep,
    liveActivityLabel,
    stillWorkingLine,
    toolStepMessage,
    toolStepVerb,
    workingLine,
};

import { exhaustSwitchCase } from "@bilda/utils";
import type { MessageDescriptor } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import type { LiveActivity, MaterialKind } from "./types.js";

type DoingCopy = {
    readonly doing: MessageDescriptor;
    readonly doingOn?: (object: string) => MessageDescriptor;
};

type ToolStepCopy = DoingCopy & {
    readonly done: MessageDescriptor;
    readonly doneCounted?: (count: number) => MessageDescriptor;
    readonly byMaterialKind?: Readonly<Record<MaterialKind, DoingCopy>>;
};

const toolSteps: Readonly<Record<string, ToolStepCopy>> = {
    // resource
    structure_as_pqbl_torus: {
        doing: msg`Writing quiz questions from the source`,
        done: msg`Wrote quiz questions from the source`,
    },
    list_codec_paths: {
        doing: msg`Checking what formats are available`,
        done: msg`Checked what formats are available`,
    },
    list_sources: {
        doing: msg`Looking through your sources`,
        done: msg`Looked through your sources`,
    },
    read_source: {
        doing: msg`Reading the source`,
        doingOn: (object) => msg`Reading source: ${object}`,
        done: msg`Read the source`,
        doneCounted: (count) =>
            msg`Read ${plural(count, { one: "the source", other: "# sources" })}`,
    },
    read_source_chunk: {
        doing: msg`Reading more of the source`,
        doingOn: (object) => msg`Reading on in: ${object}`,
        done: msg`Read more of the source`,
        doneCounted: (count) =>
            msg`Read more of ${plural(count, { one: "the source", other: "# sources" })}`,
    },
    search_content: {
        doing: msg`Searching inside your sources and materials`,
        done: msg`Searched inside your sources and materials`,
    },
    search_source: {
        doing: msg`Looking something up in the source`,
        done: msg`Looked something up in the source`,
    },
    create_source: {
        doing: msg`Adding a new source`,
        doingOn: (object) => msg`Adding source: ${object}`,
        done: msg`Added a new source`,
    },
    create_material_from_source: {
        doing: msg`Creating a material`,
        doingOn: (object) => msg`Creating material: ${object}`,
        done: msg`Created a material`,
        byMaterialKind: {
            course: {
                doing: msg`Creating a quiz`,
                doingOn: (object) => msg`Creating quiz: ${object}`,
            },
            freeform: {
                doing: msg`Writing notes`,
                doingOn: (object) => msg`Writing notes: ${object}`,
            },
        },
    },
    duplicate_material: {
        doing: msg`Copying the material`,
        doingOn: (object) => msg`Copying material: ${object}`,
        done: msg`Copied the material`,
    },
    read_material: {
        doing: msg`Reading the material`,
        doingOn: (object) => msg`Reading material: ${object}`,
        done: msg`Read the material`,
        doneCounted: (count) =>
            msg`Read ${plural(count, { one: "the material", other: "# materials" })}`,
    },
    create_package_from_material: {
        doing: msg`Packaging the material`,
        doingOn: (object) => msg`Packaging material: ${object}`,
        done: msg`Packaged the material`,
    },
    export_package: {
        doing: msg`Exporting the package`,
        doingOn: (object) => msg`Exporting: ${object}`,
        done: msg`Exported the package`,
    },
    list_resources: {
        doing: msg`Checking what you have`,
        done: msg`Checked what you have`,
    },
    evaluate_material_linguistics: {
        doing: msg`Checking the reading level`,
        doingOn: (object) => msg`Checking the reading level of: ${object}`,
        done: msg`Checked the reading level`,
    },
    evaluate_material_grounding: {
        doing: msg`Checking the material against its sources`,
        doingOn: (object) => msg`Checking against the sources: ${object}`,
        done: msg`Checked the material against its sources`,
    },
    find_element: {
        doing: msg`Finding the right spot in the material`,
        doingOn: (object) => msg`Finding the right spot in: ${object}`,
        done: msg`Found the right spot in the material`,
    },
    // resource lifecycle (D5)
    show_resource: {
        doing: msg`Looking up the resource`,
        doingOn: (object) => msg`Looking at: ${object}`,
        done: msg`Looked up the resource`,
    },
    rename_resource: {
        doing: msg`Renaming the resource`,
        doingOn: (object) => msg`Renaming: ${object}`,
        done: msg`Renamed the resource`,
    },
    delete_resource: {
        doing: msg`Deleting the resource`,
        doingOn: (object) => msg`Deleting: ${object}`,
        done: msg`Deleted the resource`,
    },
    restore_resource: {
        doing: msg`Restoring the resource`,
        doingOn: (object) => msg`Restoring: ${object}`,
        done: msg`Restored the resource`,
    },
    restore_resource_version: {
        doing: msg`Restoring an earlier version`,
        doingOn: (object) => msg`Restoring an earlier version of: ${object}`,
        done: msg`Restored an earlier version`,
    },
    confirm_resource: {
        doing: msg`Marking it as ready`,
        doingOn: (object) => msg`Marking as ready: ${object}`,
        done: msg`Marked it as ready`,
    },
    reupload_source_content: {
        doing: msg`Restoring the source file`,
        doingOn: (object) => msg`Restoring the file for: ${object}`,
        done: msg`Restored the source file`,
    },
    list_proposals: {
        doing: msg`Looking through pending edits`,
        doingOn: (object) => msg`Looking through pending edits in: ${object}`,
        done: msg`Looked through pending edits`,
    },
    cite_source: {
        doing: msg`Citing the source`,
        doingOn: (object) => msg`Citing source: ${object}`,
        done: msg`Cited the source`,
        doneCounted: (count) =>
            msg`Cited ${plural(count, { one: "the source", other: "# sources" })}`,
    },

    // materialEdit: "a part of the material", not the domain noun "element".
    // Their object is the material, so "<doing>: <title>" reads right.
    update_element_text: {
        doing: msg`Proposing a wording change`,
        done: msg`Proposed a wording change`,
    },
    update_element_attrs: {
        doing: msg`Proposing a detail change`,
        done: msg`Proposed a detail change`,
    },
    insert_element: {
        doing: msg`Adding a new part to the material`,
        doingOn: (object) => msg`Adding a new part to: ${object}`,
        done: msg`Added a new part to the material`,
    },
    insert_child_element: {
        doing: msg`Adding a new part inside the material`,
        doingOn: (object) => msg`Adding a new part to: ${object}`,
        done: msg`Added a new part inside the material`,
    },
    move_element: {
        doing: msg`Moving a part of the material`,
        doingOn: (object) => msg`Moving a part in: ${object}`,
        done: msg`Moved a part of the material`,
    },
    move_child_element: {
        doing: msg`Moving a part inside the material`,
        doingOn: (object) => msg`Moving a part in: ${object}`,
        done: msg`Moved a part inside the material`,
    },
    remove_element: {
        doing: msg`Removing a part of the material`,
        doingOn: (object) => msg`Removing a part from: ${object}`,
        done: msg`Removed a part of the material`,
    },
    wrap_element: {
        doing: msg`Grouping a part of the material`,
        doingOn: (object) => msg`Grouping a part in: ${object}`,
        done: msg`Grouped a part of the material`,
    },
    unwrap_element: {
        doing: msg`Ungrouping a part of the material`,
        doingOn: (object) => msg`Ungrouping a part in: ${object}`,
        done: msg`Ungrouped a part of the material`,
    },
    // structure (across materials)
    group_into_module: {
        doing: msg`Putting parts in a new module`,
        doingOn: (object) => msg`Putting parts in a new module: ${object}`,
        done: msg`Put parts in a new module`,
    },
    group_into_course: {
        doing: msg`Putting parts in a new course`,
        doingOn: (object) => msg`Putting parts in a new course: ${object}`,
        done: msg`Put parts in a new course`,
    },
    add_to_container: {
        doing: msg`Adding parts`,
        done: msg`Added parts`,
    },
    take_out_of_container: {
        doing: msg`Taking parts out`,
        done: msg`Took parts out`,
    },
    reorder_part: {
        doing: msg`Changing the order of the parts`,
        done: msg`Changed the order of the parts`,
    },
    // the project itself
    create_project: {
        doing: msg`Setting up a project`,
        doingOn: (object) => msg`Setting up project: ${object}`,
        done: msg`Set up a project`,
    },
    rename_project: {
        doing: msg`Renaming the project`,
        doingOn: (object) => msg`Renaming the project to: ${object}`,
        done: msg`Renamed the project`,
    },
    archive_project: {
        doing: msg`Archiving the project`,
        done: msg`Archived the project`,
    },
    restore_project: {
        doing: msg`Restoring the project`,
        done: msg`Restored the project`,
    },
    delete_project: {
        doing: msg`Deleting the project`,
        done: msg`Deleted the project`,
    },

    // file
    create_file: {
        doing: msg`Saving a file`,
        doingOn: (object) => msg`Saving file: ${object}`,
        done: msg`Saved a file`,
    },
    list_files: {
        doing: msg`Looking through your files`,
        done: msg`Looked through your files`,
    },
    read_file_metadata: {
        doing: msg`Checking the file's details`,
        doingOn: (object) => msg`Checking file: ${object}`,
        done: msg`Checked the file's details`,
    },
    update_file_description: {
        doing: msg`Updating the file's description`,
        doingOn: (object) => msg`Describing file: ${object}`,
        done: msg`Updated the file's description`,
    },
    promote_file_to_source_by_ai: {
        doing: msg`Turning the file into a source`,
        doingOn: (object) => msg`Turning into a source: ${object}`,
        done: msg`Turned the file into a source`,
    },
    promote_file_to_source_manually: {
        doing: msg`Turning the file into a source`,
        doingOn: (object) => msg`Turning into a source: ${object}`,
        done: msg`Turned the file into a source`,
    },
    delete_file: {
        doing: msg`Deleting the file`,
        doingOn: (object) => msg`Deleting file: ${object}`,
        done: msg`Deleted the file`,
    },

    // annotation
    place_annotation: {
        doing: msg`Adding a note`,
        doingOn: (object) => msg`Adding a note to: ${object}`,
        done: msg`Added a note`,
    },
    place_highlight: {
        doing: msg`Highlighting a passage`,
        doingOn: (object) => msg`Highlighting a passage in: ${object}`,
        done: msg`Highlighted a passage`,
    },
    list_annotations: {
        doing: msg`Looking through the notes`,
        doingOn: (object) => msg`Looking through the notes in: ${object}`,
        done: msg`Looked through the notes`,
    },
    delete_annotation: {
        doing: msg`Removing a note`,
        done: msg`Removed a note`,
    },

    // conversation lifecycle (D5)
    show_conversation: {
        doing: msg`Reading another conversation`,
        done: msg`Read another conversation`,
    },
    list_conversations: {
        doing: msg`Looking through your conversations`,
        done: msg`Looked through your conversations`,
    },
    rename_conversation: {
        doing: msg`Renaming the conversation`,
        doingOn: (object) => msg`Renaming the conversation to: ${object}`,
        done: msg`Renamed the conversation`,
    },
    delete_conversation: {
        doing: msg`Deleting the conversation`,
        done: msg`Deleted the conversation`,
    },

    // kb
    query_triples: {
        doing: msg`Looking up how things connect`,
        done: msg`Looked up how things connect`,
    },
    describe_entity: {
        doing: msg`Looking up a detail`,
        done: msg`Looked up a detail`,
    },
    query_path: {
        doing: msg`Tracing how things connect`,
        done: msg`Traced how things connect`,
    },
    assert_triple: {
        doing: msg`Recording a fact`,
        done: msg`Recorded a fact`,
    },
    // Retired tools (KB slice G): kept so older chats' steps still read.
    add_citation: {
        doing: msg`Linking the material to its source`,
        doingOn: (object) => msg`Adding a citation to: ${object}`,
        done: msg`Linked the material to its source`,
    },
    retract_citation: {
        doing: msg`Removing a citation`,
        doingOn: (object) => msg`Removing a citation from: ${object}`,
        done: msg`Removed a citation`,
    },

    // claims (the knowledge bank)
    record_claim: {
        doing: msg`Noting what the source says`,
        done: msg`Noted what the source says`,
    },
    revise_claim_wording: {
        doing: msg`Rewording a noted fact`,
        done: msg`Reworded a noted fact`,
    },
    retract_claim: {
        doing: msg`Removing a noted fact`,
        done: msg`Removed a noted fact`,
    },
    list_claims: {
        doing: msg`Checking what is already noted`,
        done: msg`Checked what is already noted`,
    },
    link_claim: {
        doing: msg`Tying a noted fact to what it concerns`,
        done: msg`Tied a noted fact to what it concerns`,
    },
    unlink_claim: {
        doing: msg`Untying a noted fact from what it concerns`,
        done: msg`Untied a noted fact from what it concerns`,
    },
    cite_claim: {
        doing: msg`Backing a statement with a noted fact`,
        done: msg`Backed a statement with a noted fact`,
    },
    uncite_claim: {
        doing: msg`Removing a statement's backing`,
        done: msg`Removed a statement's backing`,
    },

    // learning
    list_learning_contexts: {
        doing: msg`Looking through your learning contexts`,
        done: msg`Looked through your learning contexts`,
    },
    get_learning_context: {
        doing: msg`Checking the learning context`,
        done: msg`Checked the learning context`,
    },
    list_concepts: {
        doing: msg`Looking through the concepts`,
        done: msg`Looked through the concepts`,
    },
    get_concept: {
        doing: msg`Checking a concept`,
        done: msg`Checked a concept`,
    },
    list_terms: {
        doing: msg`Looking through the terms`,
        done: msg`Looked through the terms`,
    },
    list_objectives: {
        doing: msg`Looking through the objectives`,
        done: msg`Looked through the objectives`,
    },
    get_objective: {
        doing: msg`Checking an objective`,
        done: msg`Checked an objective`,
    },
    list_learner_profiles: {
        doing: msg`Checking who you're teaching`,
        done: msg`Checked who you're teaching`,
    },
    list_language_profiles: {
        doing: msg`Checking the language settings`,
        done: msg`Checked the language settings`,
    },
    evaluate_concept_map: {
        doing: msg`Checking how the concepts fit together`,
        done: msg`Checked how the concepts fit together`,
    },
    create_learning_context: {
        doing: msg`Setting up a learning context`,
        doingOn: (object) => msg`Setting up learning context: ${object}`,
        done: msg`Set up a learning context`,
    },
    create_learner_profile: {
        doing: msg`Describing the learners`,
        done: msg`Described the learners`,
    },
    create_language_profile: {
        doing: msg`Setting up a language profile`,
        done: msg`Set up a language profile`,
    },
    attach_learner_profile_to_context: {
        doing: msg`Attaching the learner profile`,
        done: msg`Attached the learner profile`,
    },
    create_concept: {
        doing: msg`Adding a concept`,
        done: msg`Added a concept`,
    },
    create_objective: {
        doing: msg`Adding an objective`,
        done: msg`Added an objective`,
    },
    create_term: { doing: msg`Adding a term`, done: msg`Added a term` },
    attach_concept_to_context: {
        doing: msg`Attaching the concept`,
        done: msg`Attached the concept`,
    },
    attach_objective_to_context: {
        doing: msg`Attaching the objective`,
        done: msg`Attached the objective`,
    },
    add_prerequisite: {
        doing: msg`Recording a prerequisite`,
        done: msg`Recorded a prerequisite`,
    },
    objective_touches_concept: {
        doing: msg`Linking the objective to a concept`,
        done: msg`Linked the objective to a concept`,
    },
    ground_conversation: {
        doing: msg`Binding the learning context to this conversation`,
        done: msg`Bound the learning context to this conversation`,
    },
    // goals (D55 S2, D56)
    add_goal: {
        doing: msg`Adding the goal to this chat`,
        done: msg`Added the goal to this chat`,
    },
    remove_goal: {
        doing: msg`Removing the goal from this chat`,
        done: msg`Removed the goal from this chat`,
    },
    set_goal_purpose: {
        doing: msg`Stating what the goal is for`,
        done: msg`Stated what the goal is for`,
    },
    clear_goal_purpose: {
        doing: msg`Removing the goal's purpose`,
        done: msg`Removed the goal's purpose`,
    },
    link_requirement_document: {
        doing: msg`Linking the goal to its requirement document`,
        done: msg`Linked the goal to its requirement document`,
    },
    unlink_requirement_document: {
        doing: msg`Unlinking the requirement document`,
        done: msg`Unlinked the requirement document`,
    },
    trace_objective_to_requirement: {
        doing: msg`Finding the objective in the requirement document`,
        done: msg`Found the objective in the requirement document`,
    },
    untrace_objective: {
        doing: msg`Removing where the objective comes from`,
        done: msg`Removed where the objective comes from`,
    },
    link_term_definition: {
        doing: msg`Linking the term to its definition in the source`,
        done: msg`Linked the term to its definition in the source`,
    },
    unlink_term_definition: {
        doing: msg`Removing the term's source definition`,
        done: msg`Removed the term's source definition`,
    },
    mark_part_explains: {
        doing: msg`Marking what the material explains`,
        done: msg`Marked what the material explains`,
    },
    unmark_part_explains: {
        doing: msg`Removing an explains mark`,
        done: msg`Removed an explains mark`,
    },
    mark_part_practises: {
        doing: msg`Marking what the questions practise`,
        done: msg`Marked what the questions practise`,
    },
    unmark_part_practises: {
        doing: msg`Removing a practises mark`,
        done: msg`Removed a practises mark`,
    },
    update_objective: {
        doing: msg`Rewording the learning objective`,
        done: msg`Reworded the learning objective`,
    },
    approve_objectives: {
        doing: msg`Recording your approval of the learning objectives`,
        done: msg`Recorded your approval of the learning objectives`,
    },
    approve_concepts: {
        doing: msg`Recording your approval of the concepts`,
        done: msg`Recorded your approval of the concepts`,
    },
    approve_structure: {
        doing: msg`Recording your approval of the structure`,
        done: msg`Recorded your approval of the structure`,
    },
    detach_objective: {
        doing: msg`Removing the learning objective from the goal`,
        done: msg`Removed the learning objective from the goal`,
    },
    link_sub_goal: {
        doing: msg`Putting the goal under a broader goal`,
        done: msg`Put the goal under a broader goal`,
    },
    unlink_sub_goal: {
        doing: msg`Taking the sub-goal out from its goal`,
        done: msg`Took the sub-goal out from its goal`,
    },
    link_goal_source: {
        doing: msg`Adding the source to the goal`,
        done: msg`Added the source to the goal`,
    },
    unlink_goal_source: {
        doing: msg`Removing the source from the goal`,
        done: msg`Removed the source from the goal`,
    },
    // the concepts stage (D55 S10)
    define_term: {
        doing: msg`Defining the term`,
        done: msg`Defined the term`,
    },
    redefine_term: {
        doing: msg`Changing the term's definition`,
        done: msg`Changed the term's definition`,
    },
    clear_term_definition: {
        doing: msg`Removing the term's definition`,
        done: msg`Removed the term's definition`,
    },
    rename_term: {
        doing: msg`Renaming the term`,
        done: msg`Renamed the term`,
    },
    detach_concept: {
        doing: msg`Removing the term from the goal`,
        done: msg`Removed the term from the goal`,
    },
    link_objective_concept: {
        doing: msg`Linking the term to a learning objective`,
        done: msg`Linked the term to a learning objective`,
    },
    unlink_objective_concept: {
        doing: msg`Unlinking the term from a learning objective`,
        done: msg`Unlinked the term from a learning objective`,
    },
    link_narrower_concept: {
        doing: msg`Putting the term under a broader term`,
        done: msg`Put the term under a broader term`,
    },
    unlink_narrower_concept: {
        doing: msg`Taking the term out from under a broader term`,
        done: msg`Took the term out from under a broader term`,
    },
    link_related_concept: {
        doing: msg`Linking related terms`,
        done: msg`Linked related terms`,
    },
    unlink_related_concept: {
        doing: msg`Unlinking related terms`,
        done: msg`Unlinked related terms`,
    },
    // learning lifecycle (D5)
    unground_conversation: {
        doing: msg`Clearing the audience`,
        done: msg`Cleared the audience`,
    },
    rename_learning_context: {
        doing: msg`Renaming the learning context`,
        doingOn: (object) => msg`Renaming the learning context to: ${object}`,
        done: msg`Renamed the learning context`,
    },
    update_language_profile: {
        doing: msg`Updating the language settings`,
        done: msg`Updated the language settings`,
    },
    introduce_to_context: {
        doing: msg`Adding to this chat's files`,
        doingOn: (object) => msg`Adding to this chat's files: ${object}`,
        done: msg`Added to this chat's files`,
    },
    drop_from_context: {
        doing: msg`Removing from this chat's files`,
        doingOn: (object) => msg`Removing from this chat's files: ${object}`,
        done: msg`Removed from this chat's files`,
    },
    clear_context: {
        doing: msg`Clearing this chat's files`,
        done: msg`Cleared this chat's files`,
    },
    show_context: {
        doing: msg`Checking this chat's files`,
        done: msg`Checked this chat's files`,
    },

    // generation (a material made of parts)
    plan_course: {
        doing: msg`Starting a new course`,
        done: msg`Started a new course`,
    },
    plan_parts: {
        doing: msg`Structuring the material`,
        doingOn: (object) => msg`Structuring: ${object}`,
        done: msg`Structured the material`,
    },
    start_generation: {
        doing: msg`Starting to write the parts`,
        doingOn: (object) => msg`Starting to write: ${object}`,
        done: msg`Started writing the parts`,
    },
    stop_generation: {
        doing: msg`Stopping the writing`,
        doingOn: (object) => msg`Stopping the writing of: ${object}`,
        done: msg`Stopped the writing`,
    },
    revise_part: {
        doing: msg`Asking for a new version of a part`,
        done: msg`Asked for a new version of a part`,
    },
    add_builds_on: {
        doing: msg`Linking a part to what it builds on`,
        done: msg`Linked a part to what it builds on`,
    },
    remove_builds_on: {
        doing: msg`Unlinking a part from what it built on`,
        done: msg`Unlinked a part from what it built on`,
    },
    mark_still_fine: {
        doing: msg`Keeping a part as it is`,
        done: msg`Kept a part as it is`,
    },
    revise_brief: {
        doing: msg`Changing what a part should contain`,
        done: msg`Changed what a part should contain`,
    },
    create_from_sources: {
        doing: msg`Creating from the sources`,
        done: msg`Started writing from the sources`,
    },
    use_newer_version: {
        doing: msg`Switching a part to a newer version`,
        done: msg`Switched a part to a newer version`,
    },
    keep_version: {
        doing: msg`Keeping a part's version`,
        done: msg`Kept a part's version`,
    },

    // understanding
    read_outline: {
        doing: msg`Looking over the source's outline`,
        doingOn: (object) => msg`Reading the outline of: ${object}`,
        done: msg`Looked over the source's outline`,
        doneCounted: (count) =>
            msg`Looked over ${plural(count, { one: "the source's outline", other: "the outlines of # sources" })}`,
    },
    read_passages: {
        doing: msg`Looking up saved quotes`,
        doingOn: (object) => msg`Looking up saved quotes from: ${object}`,
        done: msg`Looked up saved quotes`,
    },
    read_journal: {
        doing: msg`Looking back in the journal`,
        done: msg`Looked back in the journal`,
    },
    // skills: the guide to a method or a language the work calls for
    read_skill: {
        doing: msg`Reading up before I start`,
        done: msg`Read up before starting`,
    },
    // standing instructions: what the Author always wants (#398)
    add_instruction: {
        doing: msg`Noting what you always want`,
        done: msg`Noted what you always want`,
    },
    revise_instruction: {
        doing: msg`Rewording one of your instructions`,
        done: msg`Reworded one of your instructions`,
    },
    remove_instruction: {
        doing: msg`Removing one of your instructions`,
        done: msg`Removed one of your instructions`,
    },
    rescope_instruction: {
        doing: msg`Moving one of your instructions`,
        done: msg`Moved one of your instructions`,
    },
    propose_instruction: {
        doing: msg`Suggesting something to keep in mind`,
        done: msg`Suggested something to keep in mind`,
    },
    accept_instruction_proposal: {
        doing: msg`Keeping what you agreed to`,
        done: msg`Kept what you agreed to`,
    },
    accept_edited_instruction_proposal: {
        doing: msg`Keeping it in your words`,
        done: msg`Kept it in your words`,
    },
    decline_instruction_proposal: {
        doing: msg`Dropping the suggestion`,
        done: msg`Dropped the suggestion`,
    },
    revise_outline_entry: {
        doing: msg`Updating my notes on a section`,
        doingOn: (object) => msg`Updating my notes on: ${object}`,
        done: msg`Updated my notes on a section`,
    },
    add_source_note: {
        doing: msg`Making a note about the source`,
        doingOn: (object) => msg`Making a note about: ${object}`,
        done: msg`Made a note about the source`,
    },
    note_passage: {
        doing: msg`Saving a quote from the source`,
        doingOn: (object) => msg`Saving a quote from: ${object}`,
        done: msg`Saved a quote from the source`,
    },
    rebuild_understanding: {
        doing: msg`Re-reading the source`,
        doingOn: (object) => msg`Re-reading: ${object}`,
        done: msg`Started re-reading the source`,
    },

    // sendTurn's epilogue meta-tools: never executed, but the live row names
    // them while the model composes one (D51).
    _request_information: {
        doing: msg`Preparing a few questions for you`,
        done: msg`Prepared a few questions for you`,
    },
    _offer_actions: {
        doing: msg`Suggesting next steps`,
        done: msg`Suggested next steps`,
    },
};

// A tool the table doesn't (yet) know by name still renders as something a
// reader can trust, never its raw id: the same generic pair in both tenses.
const genericToolStep: ToolStepCopy = {
    doing: msg`Working`,
    done: msg`Worked on it`,
};

const toolStepCopy = (toolName: string): ToolStepCopy =>
    toolSteps[toolName] ?? genericToolStep;

// The present phrase for a running step (no object).
const toolStepMessage = (toolName: string): MessageDescriptor =>
    toolStepCopy(toolName).doing;

// The doing/done pair for the activity trail (dogfood F18: ToolActivityView
// reads present while a call runs, past once settled). `pastCounted` names how
// many distinct objects the merged calls touched (G10).
type ToolStepVerbMessages = {
    readonly present: MessageDescriptor;
    readonly past: MessageDescriptor;
    readonly pastCounted?: (count: number) => MessageDescriptor;
};

const toolStepVerb = (toolName: string): ToolStepVerbMessages => {
    const { doing, done, doneCounted } = toolStepCopy(toolName);
    const pair = { present: doing, past: done };
    return doneCounted === undefined
        ? pair
        : { ...pair, pastCounted: doneCounted };
};

// === First-person step line (agent voice) ===================================
// An expanded row reads as a short agent message ("Jag läser källan"), not a
// bare label. Composed from the already-localized verb phrase rather than a
// third first-person form per tool: every phrase above starts with its verb
// (never a proper noun), so lowering its first letter is safe in en and sv.
const lowerFirst = (s: string): string =>
    s.length === 0 ? s : s[0].toLowerCase() + s.slice(1);

const firstPersonStep = (
    phrase: string,
    tense: "present" | "past",
): MessageDescriptor => {
    const step = lowerFirst(phrase);
    return tense === "present" ? msg`I'm ${step}` : msg`I ${step}`;
};

// === The live row (D51) =====================================================
// What the agent is doing RIGHT NOW, with its object when known ("Reading
// source: alvesson.pdf", "Creating quiz: Sannolikhetslära"). A creating call
// reads its kind's copy once the input reveals the kind. The model's own time
// reads as planning or writing. `t` localizes (the caller's `i18n._`).
const liveActivityLabel = (
    activity: LiveActivity,
    t: (message: MessageDescriptor) => string,
): string => {
    switch (activity.type) {
        case "thinking":
            return t(msg`Planning the next step`);
        case "writing_reply":
            return t(msg`Writing a reply`);
        case "tool": {
            const copy = toolStepCopy(activity.toolName);
            const { doing, doingOn } =
                activity.materialKind === undefined
                    ? copy
                    : (copy.byMaterialKind?.[activity.materialKind] ?? copy);
            const { object } = activity;
            if (object === undefined) return t(doing);
            if (doingOn !== undefined) return t(doingOn(object));
            const verb = t(doing);
            return t(msg`${verb}: ${object}`);
        }
        default:
            return exhaustSwitchCase(activity);
    }
};

// === Working lines (WL-1) ===================================================
// A status line names the task, never the actor: "Creating a quiz…", not "The
// assistant is working…". The task is a live label above (per-tool copy, never
// free model text or a raw tool id); the agent colour says who is doing it.
const workingLine = (task: string): MessageDescriptor => msg`${task}…`;

// A long wait: the same task, still running ("Still creating a quiz…").
const stillWorkingLine = (task: string): MessageDescriptor =>
    msg`Still ${lowerFirst(task)}…`;
